import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Button } from "@nosible/shared/components/ui/button";
import { useSession } from "@/account/lib/auth-client";
import { ArrowLeft, Check, UserRound, X } from "lucide-react";
import { trpc } from "@/account/lib/trpc";

import {ENTERPRISE_TRIAL_URL} from "@/shared/lib/planCatalog";

function displayPlanName(name: string): "Free" | "Enterprise" | string {
  const normalized = name.trim().toLowerCase();
  if (normalized === "free") return "Free";
  if (normalized.includes("enterprise")) return "Enterprise";
  return name;
}

type SelectedPlanInfo = {
  name: string;
  isFree: boolean;
};

const PLAN_FEATURES = [
  { key: "api-keys", label: "API keys", free: "API keys", enterprise: "Up to 20 API keys", freeAvailable: true },
  { key: "technical-support", label: "Technical support", free: "Technical support", enterprise: "Technical support", freeAvailable: false },
  { key: "s3-access", label: "Bulk access via S3", free: "Bulk access via S3", enterprise: "Bulk access via S3", freeAvailable: false },
  { key: "on-prem", label: "On-prem options", free: "On-prem options", enterprise: "On-prem options", freeAvailable: false },
  { key: "rate-limits", label: "Higher rate limits", free: "Higher rate limits", enterprise: "Higher rate limits", freeAvailable: false },
] as const;

export function SubscriptionUpgrade() {
  const navigate = useNavigate();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const { data: session } = useSession();
  const [selectedPlan, setSelectedPlan] = useState<SelectedPlanInfo | null>(null);
  const [showFreeConfirmation, setShowFreeConfirmation] = useState(false);

  // Get subscription info by ID
  const {
    data: subscriptionInfo,
    isLoading: subscriptionLoading,
    error: subscriptionError,
  } = trpc.subscription.getSubscription.useQuery(
    { subscriptionId: subscriptionId! },
    { enabled: !!session && !!subscriptionId }
  );

  const {
    data: plans,
    isLoading: plansLoading,
  } = trpc.subscription.getActivePlans.useQuery(undefined, {
    enabled: !!session,
  });

  const { data: apiKeys } = trpc.subscription.apiKey.listApiKeys.useQuery(
    { subscriptionId: subscriptionId! },
    { enabled: !!session && !!subscriptionId },
  );

  // For free plan downgrades - upgrade in place
  const downgradeToFreePlan = trpc.subscription.mutation.downgradeToFreePlan.useMutation({
    onSuccess: () => {
      setShowFreeConfirmation(false);
      setSelectedPlan(null);
      // Redirect to success page with subscription ID
      navigate(`/subscriptions/${subscriptionId}/success`);
    },
  });

  const isLoading = subscriptionLoading || plansLoading;

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  if (subscriptionError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-red-400">{subscriptionError.message}</div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  if (!plans) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-chromium-mist">Unable to load plans</div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  const currentPlan = displayPlanName(subscriptionInfo?.plan.name || "Free");
  const ownerName = subscriptionInfo?.owner?.name || session?.user?.name || session?.user?.email;
  const isOrgUpgrade = subscriptionInfo?.owner?.type === "organization";
  const canModify = subscriptionInfo?.canModify ?? false;
  const freePlan = plans.find((plan) => displayPlanName(plan.name) === "Free");
  const enterprisePlan = plans.find((plan) => displayPlanName(plan.name) === "Enterprise");
  const enterpriseApiKeyCount = apiKeys?.length ?? subscriptionInfo?.apiKeyLimits ?? 0;
  const freeApiKeyCount = freePlan?.defaultApiKeyLimit ?? 0;

  if (!freePlan || !enterprisePlan) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-chromium-mist">Unable to load the Free and Enterprise plans</div>
      </div>
    );
  }

  const handleSelectPlan = (planName: string, monthlyPriceCents: number) => {
    setSelectedPlan({ name: displayPlanName(planName), isFree: monthlyPriceCents === 0 });
    setShowFreeConfirmation(true);
  };

  const handleConfirmUpgrade = () => {
    if (!selectedPlan || !subscriptionId || !canModify) return;

    if (selectedPlan.isFree) {
      downgradeToFreePlan.mutate({ subscriptionId });
    }
  };

  const isPending = downgradeToFreePlan.isPending;
  const isEnterpriseCurrent = currentPlan === "Enterprise";

  return (
    <div className="account-ui-page subscription-upgrade-page">
      <header className="subscription-upgrade__header">
        <nav className="subscription-upgrade__breadcrumb" aria-label="Breadcrumb">
          <Link to={`/subscriptions/${subscriptionId}`}>
            ACCOUNT / SUBSCRIPTIONS / {subscriptionInfo?.plan.name?.toUpperCase() || "ENTERPRISE ACCESS"}
          </Link>
          <span aria-hidden="true">/</span>
          <span>CHOOSE PLAN</span>
        </nav>
        <h1>CHOOSE YOUR PLAN</h1>
        <p>Free for exploring NOSIBLE. Enterprise for production access and higher limits.</p>
        <div className="subscription-upgrade__current-plan">
          <UserRound aria-hidden="true" />
          <span>{isOrgUpgrade ? "ORGANIZATION" : "PERSONAL"}</span>
          <i aria-hidden="true">·</i>
          <span>{ownerName?.toUpperCase()}</span>
          <i aria-hidden="true">·</i>
          <span>CURRENT PLAN</span>
          <strong>{currentPlan.toUpperCase()}</strong>
        </div>
      </header>

      <section className="subscription-upgrade__plans" aria-label="Available plans">
        <article className="subscription-upgrade__plan">
          <div className="subscription-upgrade__plan-top">
            <span className="subscription-upgrade__index">/01</span>
          </div>
          <h2>Free</h2>
          <p className="subscription-upgrade__description">Start exploring NOSIBLE with basic limits.</p>
          <div className="subscription-upgrade__price">
            <strong>$0</strong>
            <span>/ MONTH</span>
          </div>
          <div className="subscription-upgrade__divider" />
          <span className="subscription-upgrade__eyebrow">FEATURES</span>
          <ul className="subscription-upgrade__features">
            {PLAN_FEATURES.map((feature) => (
              <li key={feature.key} className={feature.freeAvailable ? undefined : "subscription-upgrade__feature--unavailable"}>
                {feature.freeAvailable ? <Check aria-hidden="true" /> : <X aria-hidden="true" />}
                <span>{feature.key === "api-keys" ? `${freeApiKeyCount} API keys` : feature.freeAvailable ? feature.free : feature.label}</span>
              </li>
            ))}
          </ul>
          <div className="subscription-upgrade__card-action">
            {currentPlan === "Free" ? (
              <button type="button" disabled className="subscription-upgrade__action subscription-upgrade__action--current">
                YOUR CURRENT PLAN
              </button>
            ) : showFreeConfirmation ? (
              <div className="subscription-upgrade__confirmation" role="status">
                <p><strong>Free includes {freeApiKeyCount} API keys.</strong> Your current plan has {enterpriseApiKeyCount}.</p>
                <div className="subscription-upgrade__confirmation-actions">
                  <button
                    type="button"
                    className="subscription-upgrade__confirmation-button"
                    onClick={() => {
                      setShowFreeConfirmation(false);
                      setSelectedPlan(null);
                    }}
                  >
                    KEEP ENTERPRISE
                  </button>
                  <button
                    type="button"
                    className="subscription-upgrade__confirmation-button subscription-upgrade__confirmation-button--confirm"
                    onClick={handleConfirmUpgrade}
                    disabled={isPending}
                  >
                    {isPending ? "SWITCHING..." : "CONFIRM SWITCH"}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => handleSelectPlan(freePlan.name, freePlan.monthlyPriceCents)}
                disabled={isPending || !canModify}
                className="subscription-upgrade__action"
              >
                SWITCH TO FREE
              </button>
            )}
          </div>
        </article>

        <article className="subscription-upgrade__plan">
          <div className="subscription-upgrade__plan-top">
            <span className="subscription-upgrade__index">/02</span>
            {isEnterpriseCurrent && <span className="subscription-upgrade__current-badge">CURRENT PLAN</span>}
          </div>
          <h2>Enterprise</h2>
          <p className="subscription-upgrade__description">
            Production access for teams that need higher limits and enterprise support.
          </p>
          <div className="subscription-upgrade__price">
            <strong>Tailored</strong>
            <span>PRICING</span>
          </div>
          <div className="subscription-upgrade__divider" />
          <span className="subscription-upgrade__eyebrow">FEATURES</span>
          <ul className="subscription-upgrade__features">
            {PLAN_FEATURES.map((feature) => (
              <li key={feature.key}>
                <Check aria-hidden="true" />
                <span>{feature.enterprise}</span>
              </li>
            ))}
          </ul>
          <div className="subscription-upgrade__card-action">
            {isEnterpriseCurrent ? (
              <button type="button" disabled className="subscription-upgrade__action subscription-upgrade__action--current">
                YOUR CURRENT PLAN
              </button>
            ) : (
              <a href={ENTERPRISE_TRIAL_URL} className="subscription-upgrade__action subscription-upgrade__action--enterprise">
                START ENTERPRISE TRIAL
              </a>
            )}
          </div>
        </article>
      </section>

      <section className="subscription-upgrade__help">
        <div>
          <h2>Need help choosing a plan?</h2>
          <p>Free plan changes happen here. Enterprise trials start at nosible.com.</p>
        </div>
        <a href="https://nosible.com">CONTACT OUR TEAM <span aria-hidden="true">↗</span></a>
      </section>

      {downgradeToFreePlan.error && (
        <div className="mt-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-center">
          Error: {downgradeToFreePlan.error.message}
        </div>
      )}
    </div>
  );
}
